import { FlashKindEnum } from "@/utils/flash";
import { defineStore } from "pinia";
import { v4 as uuidv4 } from "uuid";

export type FlashMessage = {
  id: string;
  message: string;
  type: FlashKindEnum;
};
export type CreateFlashMessage = Omit<FlashMessage, "id">;

type FlashMessageStoreType = {
  messages: FlashMessage[];
};

// Rails' flash keys are not our variant names. `alert` and `error` are both
// failure channels, so both map to ALERT.
//
// `notice` is deliberately NOT mapped to SUCCESS. It reads like Rails' success
// channel, but here it is the catch-all and carries plenty of non-successes:
// `general.flash.course_not_played`, `courses.index.flash.not_found`,
// `onboardings.already_done`, several backoffice "already …" notices, and
// three sites that flash `t(".flash.error")` through it. Mapping it would
// render all of those as green confirmations. They stay INFO until the
// producers use an accurate key — see the follow-up noted on #9039.
const messageType = (type: string): FlashMessage["type"] => {
  {
    if (type === "alert" || type === "error") {
      return FlashKindEnum.ALERT;
    }

    if (type === "success") {
      return FlashKindEnum.SUCCESS;
    }

    return FlashKindEnum.INFO;
  }
};

export const useFlashStore = defineStore("flash", {
  state: (): FlashMessageStoreType => {
    return {
      messages: [],
    };
  },
  actions: {
    addMessage(message: CreateFlashMessage) {
      this.messages.push({
        ...message,
        type: messageType(message.type),
        id: uuidv4(),
      });
    },
    removeMessage(id: string) {
      const index = this.messages.findIndex((message) => message.id === id);
      if (index > -1) {
        this.messages.splice(index, 1);
      }
    },
  },
});
